import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import catalogoDetalhadoConfigService from '../../services/catalogoDetalhadoConfigService';

const DEFAULT_COLUNAS_GADO = ['ordem', 'lote', 'vendedor', 'quantidade', 'tipo', 'raca', 'sexo', 'idade', 'observacoes'];
// Fluxo cavalo: colunas focadas na identidade do animal, igual aos outros
// catálogos. Limite aqui é 8 colunas (com Observações) — mais apertado que
// Leilão/Pisteiro — então o default já vem enxuto (sem O.D/Qtde).
const DEFAULT_COLUNAS_CAVALO = ['lote', 'animal', 'raca', 'data_nascimento', 'pai', 'mae', 'vendedor', 'observacoes'];

const CatalogoDetalhado = () => {
  const { organizadorAtivo } = useOrganizador();
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo';
  const { toasts, success, error, removeToast } = useToast();
  const colunasDefault = isFluxoCavalo ? DEFAULT_COLUNAS_CAVALO : DEFAULT_COLUNAS_GADO;

  const [configuracao, setConfiguracao] = useState({
    colunas_ativas: colunasDefault,
    lotes_por_pagina: 32,
    mostrar_totais: true,
    mostrar_rodape: true,
    linha_anotacoes: false,
    observacao_sempre_abaixo: false,
    obs_ocultar_linha_abaixo: false,
    ajuste_automatico_colunas: false,
    espacamento_linha: 'normal',
    fonte_tamanho: 9,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (organizadorAtivo?.id) {
      loadConfig();
    }
  }, [organizadorAtivo]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const config = await catalogoDetalhadoConfigService.getConfig(organizadorAtivo.id);
      setConfiguracao(config);
    } catch (err) {
      console.error('Erro ao carregar configuração:', err);
    } finally {
      setLoading(false);
    }
  };

  const colunasDisponiveis = [
    { id: 'ordem',        label: 'O.D',            obrigatoria: false },
    { id: 'lote',         label: 'Lote',           obrigatoria: true  },
    { id: 'vendedor',     label: 'Vendedor/Fazenda',obrigatoria: true  },
    { id: 'fazenda',      label: 'Fazenda',        obrigatoria: false },
    { id: 'quantidade',   label: 'Qtde',           obrigatoria: false },
    { id: 'tipo',         label: 'Tipo',           obrigatoria: false },
    { id: 'raca',         label: 'Raça',           obrigatoria: false },
    { id: 'sexo',         label: 'Sexo',           obrigatoria: false },
    { id: 'idade',        label: 'Idade',          obrigatoria: false },
    { id: 'peso',         label: 'Peso/@',         obrigatoria: false },
    { id: 'curral',       label: 'Curral',         obrigatoria: false },
    { id: 'observacoes',  label: 'Observações',    obrigatoria: false },
    { id: 'valor_inicial',label: 'Valor Inicial',  obrigatoria: false },
    ...(isFluxoCavalo ? [
      { id: 'animal', label: 'Animal (% + nome + registro)', obrigatoria: false },
      { id: 'data_nascimento', label: 'Nasc.', obrigatoria: false },
      { id: 'pai', label: 'Pai', obrigatoria: false },
      { id: 'mae', label: 'Mãe', obrigatoria: false },
      { id: 'registro', label: 'Registro (coluna separada)', obrigatoria: false },
      { id: 'pelagem',          label: 'Pelagem',      obrigatoria: false },
      { id: 'percentual_venda', label: '% Venda (coluna separada)', obrigatoria: false },
      { id: 'localizacao',      label: 'Localização',  obrigatoria: false },
    ] : []),
  ];

  const toggleColuna = (colunaId) => {
    const coluna = colunasDisponiveis.find(c => c.id === colunaId);
    if (coluna?.obrigatoria) return;

    const novasColunas = configuracao.colunas_ativas.includes(colunaId)
      ? configuracao.colunas_ativas.filter(id => id !== colunaId)
      : [...configuracao.colunas_ativas, colunaId];

    const temObs = novasColunas.includes('observacoes');
    const obsSempreAbaixo = temObs && !!configuracao.observacao_sempre_abaixo;
    // Com "observação sempre abaixo" ela não ocupa coluna na tabela, então libera uma coluna a mais
    const maxColunas = obsSempreAbaixo ? 10 : 8;

    if (novasColunas.length > maxColunas) {
      error(`Máximo de ${maxColunas} colunas permitidas`);
      return;
    }

    setConfiguracao(prev => ({ ...prev, colunas_ativas: novasColunas }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const configToSave = { ...configuracao, mostrar_rodape: true };
      await catalogoDetalhadoConfigService.saveConfig(organizadorAtivo.id, configToSave);
      success('Configuração salva com sucesso!');
    } catch (err) {
      console.error('Erro ao salvar:', err);
      error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    setConfiguracao({
      colunas_ativas: colunasDefault,
      lotes_por_pagina: 32,
      mostrar_totais: true,
      mostrar_rodape: true,
      linha_anotacoes: false,
      observacao_sempre_abaixo: false,
      obs_ocultar_linha_abaixo: false,
      ajuste_automatico_colunas: false,
      espacamento_linha: 'normal',
      fonte_tamanho: 9,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const temObs = configuracao.colunas_ativas.includes('observacoes');
  const obsSempreAbaixo = temObs && !!configuracao.observacao_sempre_abaixo;
  const maxColunas = obsSempreAbaixo ? 10 : 8;

  return (
    <div className="">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl px-6 py-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Configuração do Catálogo Detalhado</h1>
          <p className="text-indigo-100 mt-1">
            Personalize as colunas e layout do catálogo detalhado com vendedor + fazenda fictícia
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuração de Colunas */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Colunas do Catálogo</h2>
              <p className="text-sm text-gray-600 mb-6">
                A coluna <strong>Vendedor/Fazenda</strong> exibe o nome do vendedor e, abaixo, o nome da fazenda fictícia cadastrada no lote.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {colunasDisponiveis.map((coluna) => {
                  const isActive = configuracao.colunas_ativas.includes(coluna.id);
                  const isObrigatoria = coluna.obrigatoria;

                  return (
                    <div
                      key={coluna.id}
                      className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all cursor-pointer ${
                        isActive
                          ? 'border-indigo-200 bg-indigo-50'
                          : 'border-gray-200 bg-gray-50'
                      } ${isObrigatoria ? 'opacity-75 cursor-not-allowed' : 'hover:border-indigo-300'}`}
                      onClick={() => !isObrigatoria && toggleColuna(coluna.id)}
                    >
                      <div className="flex items-center gap-3">
                        {isActive ? (
                          <Eye className="w-5 h-5 text-indigo-600" />
                        ) : (
                          <EyeOff className="w-5 h-5 text-gray-400" />
                        )}
                        <span className={`font-medium ${isActive ? 'text-indigo-900' : 'text-gray-600'}`}>
                          {coluna.label}
                        </span>
                      </div>
                      {isObrigatoria && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                          Obrigatória
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {configuracao.colunas_ativas.includes('observacoes') && (
                <div className="flex items-center justify-between mt-4 p-3 rounded-lg border-2 border-indigo-100 bg-indigo-50">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Observação sempre na linha abaixo
                    </label>
                    <p className="text-xs text-gray-500">
                      Remove a coluna Observações da tabela e exibe o texto completo em uma linha abaixo de cada lote, com fonte maior
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={configuracao.observacao_sempre_abaixo || false}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      observacao_sempre_abaixo: e.target.checked
                    }))}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </div>
              )}

              {configuracao.colunas_ativas.includes('observacoes') && !configuracao.observacao_sempre_abaixo && !isFluxoCavalo && (
                <div className="flex items-center justify-between mt-4 p-3 rounded-lg border-2 border-indigo-100 bg-indigo-50">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Não mostrar a linha abaixo quando a observação não couber
                    </label>
                    <p className="text-xs text-gray-500">
                      A fonte da observação diminui automaticamente para caber na coluna (até 2 linhas), sem criar a linha extra abaixo do lote
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={configuracao.obs_ocultar_linha_abaixo || false}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      obs_ocultar_linha_abaixo: e.target.checked
                    }))}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </div>
              )}

              <div className="flex items-center justify-between mt-4 p-3 rounded-lg border-2 border-indigo-100 bg-indigo-50">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Ajuste automático do tamanho das colunas
                  </label>
                  <p className="text-xs text-gray-500">
                    A largura de cada coluna se adapta ao conteúdo; o espaço que sobra vai para Observações e Vendedor
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={configuracao.ajuste_automatico_colunas || false}
                  onChange={(e) => setConfiguracao(prev => ({
                    ...prev,
                    ajuste_automatico_colunas: e.target.checked
                  }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center justify-between mt-4 p-3 rounded-lg border-2 border-indigo-100 bg-indigo-50">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Altura das linhas
                  </label>
                  <p className="text-xs text-gray-500">
                    Espaço acima e abaixo do texto de cada linha do catálogo
                  </p>
                </div>
                <div className="flex gap-1">
                  {[['compacta', 'Compacta'], ['normal', 'Normal'], ['folgada', 'Folgada']].map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setConfiguracao(prev => ({ ...prev, espacamento_linha: id }))}
                      className={`px-3 h-8 rounded-md text-sm font-medium transition-colors ${
                        (configuracao.espacamento_linha || 'normal') === id
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-300 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Configurações Gerais */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações Gerais</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lotes por Página
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
                    {configuracao.lotes_por_pagina} (fixo)
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Valor fixo otimizado para impressão</p>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Mostrar Totais
                  </label>
                  <input
                    type="checkbox"
                    checked={configuracao.mostrar_totais}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      mostrar_totais: e.target.checked,
                    }))}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tamanho da Fonte
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Fonte do PDF (9 = padrão dos demais catálogos). Acima de 10 use paisagem, senão palavras quebram nas colunas estreitas</p>
                  <div className="flex gap-1">
                    {[9, 10, 11].map(sz => (
                      <button
                        key={sz}
                        type="button"
                        onClick={() => setConfiguracao(prev => ({ ...prev, fonte_tamanho: sz }))}
                        className={`w-10 h-8 rounded-md text-sm font-medium transition-colors ${
                          (configuracao.fonte_tamanho ?? 9) === sz
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {sz}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Linha de Anotações
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">Linha em branco abaixo de cada lote para o pisteiro anotar</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={configuracao.linha_anotacoes ?? false}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      linha_anotacoes: e.target.checked,
                    }))}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>Colunas ativas:</strong> {configuracao.colunas_ativas.length}/{maxColunas}</p>
                <p><strong>Lotes por página:</strong> {configuracao.lotes_por_pagina}</p>
                <p><strong>Totais:</strong> {configuracao.mostrar_totais ? 'Sim' : 'Não'}</p>
                <p><strong>Linha de Anotações:</strong> {configuracao.linha_anotacoes ? 'Sim' : 'Não'}</p>
                {temObs && (
                  <p className="text-yellow-600"><strong>⚠ Limite:</strong> {maxColunas} colunas{obsSempreAbaixo ? '' : ' (com Observações)'}</p>
                )}
              </div>
            </div>

            {/* Ações */}
            <div className="space-y-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Salvando...' : 'Salvar Configuração'}
              </button>

              <button
                onClick={resetToDefault}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
                Restaurar Padrão
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default CatalogoDetalhado;
